import { useState, useEffect, useCallback } from "react";
import { getToken } from "../../../services/tokenGET";

/**
 * Custom hook for managing quiz questions
 * Handles CRUD operations for questions
 * 
 * @param {Array} initialQuestions - Initial questions from API
 * @param {Array} speciesList - List of species for reference
 * @returns {Object} Question data and handlers
 */
export function useQuizData(initialQuestions, speciesList) {
    const [questionList, setQuestionList] = useState([]);
    const [selectedItem, setSelectedItem] = useState(null);
    const [questionText, setQuestionText] = useState("");
    const [answer1, setAnswer1] = useState("");
    const [answer2, setAnswer2] = useState("");
    const [answer3, setAnswer3] = useState("");
    const [selectedSpecies, setSelectedSpecies] = useState("");
    const [audioFile, setAudioFile] = useState(null);

    // Initialize questions
    useEffect(() => {
        if (initialQuestions) {
            setQuestionList(initialQuestions);
        }
    }, [initialQuestions]);

    const handleSelectQuestion = useCallback((question) => {
        setSelectedItem(question);
        setQuestionText(question.question);
        setAnswer1(question.correct_answer);
        setAnswer2(question.wrong_answer1);
        setAnswer3(question.wrong_answer2);
        setSelectedSpecies(question.species_id || "");
    }, []);

    const handleResetForm = () => {
        setQuestionText("");
        setSelectedSpecies("");
        setAnswer1("");
        setAnswer2("");
        setAnswer3("");
        setSelectedItem(null);
        setAudioFile(null);
    };

    const handleRemoveImage = (imageName) => {
        if (imageName === "answer 1") {
            setAnswer1("");
        } else if (imageName === "answer 2") {
            setAnswer2("");
        } else if (imageName === "answer 3") {
            setAnswer3("");
        }
    };

    const handleAudioFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setAudioFile(file);
        }
    };

    const handleSaveQuestion = async (imageFileEncoded1, imageFileEncoded2, imageFileEncoded3, questionImage) => {
        console.log("=== handleSaveQuestion called ===");
        console.log("questionImage parameter:", questionImage);
        console.log("imageFileEncoded1:", imageFileEncoded1);
        console.log("imageFileEncoded2:", imageFileEncoded2);
        console.log("imageFileEncoded3:", imageFileEncoded3);
        
        // Validation before submitting
        if (!questionText.trim()) {
            alert("Frågetexten kan inte vara tom");
            return;
        }
        if (!answer1.trim()) {
            alert("Rätt svar kan inte vara tomt");
            return;
        }
        if (!answer2.trim()) {
            alert("Fel svar 1 kan inte vara tomt");
            return;
        }
        if (!answer3.trim()) {
            alert("Fel svar 2 kan inte vara tomt");
            return;
        }
        if (!selectedSpecies) {
            alert("Du måste välja en art");
            return;
        }

        const formData = new FormData();
        formData.append("correct_answer", answer1);
        formData.append("question", questionText);
        formData.append("wrong_answer1", answer2);
        formData.append("wrong_answer2", answer3);
        formData.append("species_id", selectedSpecies);

        if (imageFileEncoded1) formData.append("answer_image1", imageFileEncoded1);
        if (imageFileEncoded2) formData.append("answer_image2", imageFileEncoded2);
        if (imageFileEncoded3) formData.append("answer_image3", imageFileEncoded3);
        if (audioFile) formData.append("audio", audioFile);
        if (questionImage) {
            console.log("Appending question_image:", questionImage);
            formData.append("question_image", questionImage);
        } else {
            console.log("NO question_image to append");
        }

        const url = selectedItem
            ? `${process.env.REACT_APP_API_URL}/api/questions/${selectedItem.id}`
            : `${process.env.REACT_APP_API_URL}/api/questions`;

        

        try {
            const token = await getToken();
            const response = await fetch(url, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'X-CSRF-TOKEN': token
                },
                body: formData,
            });

            if (!response.ok) {
                throw new Error(`Error: ${response.status} - ${response.statusText}`);
            }

            const data = await response.json();

            if (data && data.question) {
                if (selectedItem) {
                    setQuestionList(questionList.map(question =>
                        question.id === data.question.id ? { ...question, ...data.question } : question
                    ));
                } else {
                    setQuestionList([...questionList, data.question]);
                }
                alert("Fråga sparad!");
            }

            handleResetForm();
        } catch (error) {
            console.error("Error adding new question:", error);
            alert("Ett fel uppstod vid sparande");
        }
    };

    const handleDeleteQuestion = async () => {
        if (!window.confirm("Är du säker på att du vill radera denna fråga?")) {
            return;
        }

        try {
            const token = await getToken();
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/questions/${selectedItem.id}`, {
                method: 'DELETE',
                credentials: 'include',
                headers: {
                    "Content-Type": "application/json",
                    'X-CSRF-TOKEN': token
                }
            });

            if (response.status === 200) {
                setQuestionList(prevList => prevList.filter(question => question.id !== selectedItem.id));
                handleResetForm();
                alert("Fråga raderad!");
            } else {
                alert("Kunde inte radera frågan");
            }
        } catch (error) {
            console.error("Error deleting question:", error);
            alert("Ett fel uppstod vid radering");
        }
    };

    const handleDeleteQuestionImage = async () => {
        if (!selectedItem || !selectedItem.id) {
            alert("Ingen fråga vald");
            return;
        }

        if (!window.confirm("Är du säker på att du vill ta bort bilden?")) {
            return;
        }

        try {
            const token = await getToken();
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/questions/${selectedItem.id}/images`, {
                method: "DELETE",
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': token
                },
                body: JSON.stringify({
                    field: 'question_image' // TODO sent along field to delete
                }),
            });

            if (!response.ok) {
                throw new Error(`Error: ${response.status}`);
            }

            const data = await response.json();
            if (data && data.question) {
                setQuestionList(questionList.map(question =>
                    question.id === data.question.id ? { ...question, ...data.question } : question
                ));
                setSelectedItem({ ...selectedItem, question_image: null });
                alert("Bild borttagen!");
            }
        } catch (error) {
            console.error("Error deleting image:", error);
            alert("Ett fel uppstod vid borttagning");
        }
    };

    return {
        questionList,
        selectedItem,
        questionText,
        setQuestionText,
        answer1,
        setAnswer1,
        answer2,
        setAnswer2,
        answer3,
        setAnswer3,
        selectedSpecies,
        setSelectedSpecies,
        audioFile,
        handleSelectQuestion,
        handleResetForm,
        handleRemoveImage,
        handleAudioFileChange,
        handleSaveQuestion,
        handleDeleteQuestion,
        handleDeleteQuestionImage,
    };
}
